// 📄 components/Configuracion/ConfiguracionTablasZ.tsx
// Gestión de las Tablas Z (SAP, ej. vía SE16N) propias de ESTE proyecto —
// mismo patrón estructural que ConfiguracionBadisZ.tsx.
import { useEffect, useState } from "react";
import { Table2, Plus, RefreshCw, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext.tsx";
import { getTablasZ, eliminarTablaZ, TablaZ } from "../../services/tablaZService.ts";
import { solucionBadiMeta } from "../../services/catalogoBadisService.ts";
import ConfirmDialog from "../ui/ConfirmDialog.tsx";
import Alert from "../ui/Alert.tsx";
import TablaZFormModal from "./TablaZFormModal.tsx";

interface ConfiguracionTablasZProps {
  projectId: string;
  projectNombre?: string;
}

export default function ConfiguracionTablasZ({ projectId, projectNombre }: ConfiguracionTablasZProps) {
  // Borrar está restringido a Admin en firestore.rules (mismo patrón que
  // badisZ/transaccionesZ) — se oculta el botón para el resto de los miembros.
  const { profile } = useAuth();
  const esAdmin = profile?.role === "admin";
  const [tablas, setTablas] = useState<TablaZ[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState<TablaZ | null>(null);
  const [aEliminar, setAEliminar] = useState<TablaZ | null>(null);
  const [eliminando, setEliminando] = useState(false);

  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error" | "warning">("success");
  const showAlert = (msg: string, type: "success" | "error" | "warning" = "success") => {
    setAlertMessage(msg);
    setAlertType(type);
    setTimeout(() => setAlertMessage(""), 4000);
  };

  const cargar = async () => {
    setLoading(true);
    try {
      setTablas(await getTablasZ(projectId));
    } catch (err) {
      console.error("Error cargando las Tablas Z:", err);
      setError("No se pudieron cargar las Tablas Z de este proyecto.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId) { setLoading(false); return; }
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleGuardado = () => {
    const eraEdicion = !!editando;
    setModalOpen(false);
    setEditando(null);
    cargar();
    showAlert(eraEdicion ? "Tabla Z actualizada correctamente." : "Tabla Z guardada correctamente.");
  };

  const handleCancelarModal = () => {
    setModalOpen(false);
    setEditando(null);
  };

  const handleConfirmarEliminar = async () => {
    if (!aEliminar || eliminando) return;
    setEliminando(true);
    try {
      await eliminarTablaZ(projectId, aEliminar.id);
      setAEliminar(null);
      cargar();
      showAlert("Tabla Z eliminada correctamente.");
    } catch (err: any) {
      showAlert(err.message || "Error al eliminar la tabla.", "error");
    } finally {
      setEliminando(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        <p className="text-sm">Cargando Tablas Z...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Alert message={alertMessage} type={alertType} onClose={() => setAlertMessage("")} />

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-300">Tablas Z</h3>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="flex items-center gap-1.5 text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Nueva Tabla Z
        </button>
      </div>

      {tablas.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <Table2 className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">Este proyecto todavía no tiene Tablas Z propias.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {tablas.map((t) => {
            const solMeta = solucionBadiMeta(t.solucion);
            return (
              <div key={t.id} className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-2xl p-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold font-mono text-white truncate">{t.nombreTecnico}</span>
                    {t.transaccion && <span className="text-xs font-mono text-gray-400">({t.transaccion})</span>}
                  </div>
                  {t.descripcion && <p className="text-xs text-gray-400 mt-1">{t.descripcion}</p>}
                  {t.campos.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {t.campos.map((c, i) => (
                        <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-black/20 border border-white/10 text-gray-300 font-mono">
                          {c.nombre}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-amber-300 bg-amber-500/10 border-amber-500/30">
                      Z
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-indigo-300 bg-indigo-500/10 border-indigo-500/30">
                      {solMeta.label}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setEditando(t)}
                  className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-all flex-shrink-0"
                  title="Editar"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                {esAdmin && (
                  <button
                    onClick={() => setAEliminar(t)}
                    className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all flex-shrink-0"
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {(modalOpen || editando) && (
        <TablaZFormModal
          projectId={projectId}
          tablaInicial={editando}
          onGuardado={handleGuardado}
          onCancelar={handleCancelarModal}
        />
      )}

      {aEliminar && (
        <ConfirmDialog
          title="¿Eliminar esta Tabla Z?"
          message={`"${aEliminar.nombreTecnico}" se eliminará de ${projectNombre || "este proyecto"}. Esta acción no se puede deshacer.`}
          confirmLabel={eliminando ? "Eliminando..." : "Eliminar"}
          danger
          onConfirm={handleConfirmarEliminar}
          onCancel={() => setAEliminar(null)}
        />
      )}
    </div>
  );
}
